import { Button, Icon } from '@platform/ui-library';
import {
  FormEvent,
  useEffect,
  useRef,
  useState
} from 'react';
import {
  Message,
  MessageStatus,
  PinInput,
  Switch
} from '@app/form_components';
import { useServices } from '@app/hooks';
import {
  LogindWithMfaState,
  LoginEnableMfaState,
  LoginState
} from './login-state';
import { mfaErrorMessage } from './mfaErrorMessage';
import styles from './login-code-form.module.scss';

export type LoginCodeFormProps = {
  onComplete: () => void;
  state: LoginEnableMfaState | LogindWithMfaState;
  setState: (values: LoginState) => void;
};

export function LoginCodeForm({
  onComplete,
  state,
  setState
}: LoginCodeFormProps) {
  const { userService } = useServices();
  const contentRef = useRef<HTMLFormElement>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    requestAnimationFrame(() => {
      contentRef.current?.querySelector('input')?.focus();
    });
  }, []);

  const {
    code,
    enableRememberMe,
    isMfaEnabled
  } = state;
  const qrCode = (isMfaEnabled) ? null : `data:image/png;base64, ${state.qrCode}`;

  const message = (isMfaEnabled) ? (
    'Please enter the six-digit code generated by your authenticator app.'
  ) : [
    'Enable two-factor authentication for enhance account security.',
    'Scan QR code from an authenticator app and enter six-digit code before logging in.'
  ].join('\n');

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!code) {
      setErrorMessage('Enter code before proceeding.');
    }

    setIsLoading(true);
    try {
      await userService.login(state);
      onComplete();
      setIsLoading(false);
    } catch (error) {
      setErrorMessage(await mfaErrorMessage(error));
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={onSubmit} ref={contentRef}>
      <div className="box header">
        <a onClick={() => setState({ ...state, validUsernameAndPassword: false })}>
          <Icon icon='arrowLeft' />
        </a>
        <h2>
          {(!isMfaEnabled) ? 'Enable ' : ''}
          Multi-Factor Authentication
        </h2>
      </div>
      <div className="box content">
        <p className="space-pre-wrap">{message}</p>
        {(!qrCode) ? null : (
          <div className={styles.qr}>
            <img alt='QR Code' src={qrCode} />
          </div>
        )}
        <label>Enter Verification Code</label>
        <PinInput
          onComplete={(newCode) => setState({ ...state, code: newCode })}
          defaultValue={code}
        />
        <Message
          className="spacePreWrap"
          message={errorMessage}
          status={MessageStatus.error}
        />
      </div>
      <div className="box footer no-border space-between">
        <Switch
          onChange={(newEnableRememberMe) => {
            setState({ ...state, enableRememberMe: newEnableRememberMe });
          }}
          name="rememberMe"
          label="Remember me"
          value={enableRememberMe}
        />
        <Button type="submit" theme='fancy'>
          {(isLoading) ? 'Loading...' : 'Verify & Login'}
        </Button>
      </div>
    </form>
  );
}
